import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Plus,
  Trash2,
  Eye,
  RefreshCw,
  AlertTriangle,
  Play,
  Pause,
  Ban,
  Clock,
  RotateCcw,
} from 'lucide-react';
import { backofficeApi, extractErrorMessage, formatDateTime, formatNumber } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type {
  CampaignAudienceType,
  LeadStatus,
  MessageCampaignDto,
  MessageCampaignRequestDto,
  MessageCampaignStatus,
} from '@kardume/types';
import {
  Badge,
  Button,
  ActionButton,
  Modal,
  TextInput,
  Select,
  Checkbox,
  Table,
  TableContainer,
  Pagination,
  SegmentedControl,
} from '@kardume/ui';
import { useAsync } from '../../utils/useAsync';
import DataState from '../DataState';
import CampaignDetailDrawer from './CampaignDetailDrawer';
import { describeScheduleProblem, shortTime, toIsoFromInput } from './campaignSchedule';
import {
  CAMPAIGN_STATUS_LABEL,
  CAMPAIGN_STATUS_TONE,
  LEAD_STATUS_LABEL,
  LEAD_STATUSES,
} from './messagingLabels';

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  align-items: center;
  flex-wrap: wrap;

  > *:first-child {
    flex: 1;
    min-width: 200px;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const Label = styled.span`
  display: block;
  margin: 1rem 0 0.4rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(p) => p.theme.textMuted};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 130px;
  resize: vertical;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surface};
  color: ${(p) => p.theme.textPrimary};
  font: inherit;
  font-size: 0.88rem;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
  }
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem 0.75rem;
  margin-top: 0.25rem;

  @media (max-width: 620px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Preview = styled.div`
  margin-top: 0.5rem;
  padding: 0.8rem 0.95rem;
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.primaryLight};
  color: ${(p) => p.theme.textPrimary};
  font-size: 0.88rem;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
`;

const FieldHint = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${(p) => p.theme.textMuted};
`;

const Warn = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  margin: 0.6rem 0 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: ${(p) => p.theme.warning};
`;

const Scheduled = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.primary};
`;

/** `datetime-local` com a mesma casca dos TextInput do design system. */
const DateTimeInput = styled.input`
  width: 100%;
  padding: 0.65rem 0.8rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surface};
  color: ${(p) => p.theme.textPrimary};
  font: inherit;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
  }
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const STATUS_FILTER = [
  { value: '', label: 'Todos os status' },
  ...(Object.keys(CAMPAIGN_STATUS_LABEL) as MessageCampaignStatus[]).map((status) => ({
    value: status,
    label: CAMPAIGN_STATUS_LABEL[status],
  })),
];

const AUDIENCE_OPTIONS: { value: CampaignAudienceType; label: string }[] = [
  { value: 'CONTACT_LIST', label: 'Lista de contatos' },
  { value: 'LEADS', label: 'Leads (Kanban)' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * Enquanto houver campanha drenando, a listagem se atualiza sozinha.
 *
 * Sem isto, "Fila / enviadas" congela no valor de quando a aba abriu e o operador
 * fica olhando `2 / 0` sem saber se o motor parou ou se é a tela que está velha.
 * Mesmo intervalo do drawer de propósito: dois números diferentes para a mesma
 * campanha, em telas lado a lado, geram mais dúvida do que informam.
 */
const LIST_POLL_MS = 15000;

interface CampaignsTabProps {
  canManage: boolean;
  canSend: boolean;
}

interface CampaignForm {
  name: string;
  instanceId: string;
  templateId: string;
  body: string;
  audienceType: CampaignAudienceType;
  contactListId: string;
  leadStatuses: LeadStatus[];
  /**
   * Não enviar para quem já é parceiro ativo.
   *
   * Marcado por padrão: a campanha de disparo é de prospecção, e mandar oferta de entrada
   * para quem já assinou é o erro que mais custa credibilidade com o lojista.
   */
  excludePartners: boolean;
  /** Valor cru do `datetime-local`. Vazio = início manual. */
  scheduledAt: string;
}

const EMPTY_FORM: CampaignForm = {
  name: '',
  instanceId: '',
  templateId: '',
  body: '',
  audienceType: 'CONTACT_LIST',
  contactListId: '',
  leadStatuses: [],
  excludePartners: true,
  scheduledAt: '',
};

/**
 * Campanhas: montar e acompanhar.
 *
 * Note o que não existe aqui: um botão "enviar agora". Criar a campanha resolve a
 * audiência e enfileira todo mundo em rascunho; "Iniciar", no drawer de detalhe,
 * libera o drenador, que manda uma mensagem por vez no ritmo da instância. O ritmo é
 * o produto.
 */
const CampaignsTab: React.FC<CampaignsTabProps> = ({ canManage, canSend }) => {
  const { showNotification } = useNotification();

  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState<CampaignForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [rendered, setRendered] = useState('');
  const [deleting, setDeleting] = useState<MessageCampaignDto | null>(null);
  const [removing, setRemoving] = useState(false);
  /** Id da campanha em ação - trava só a linha dela, não a tabela inteira. */
  const [acting, setActing] = useState<string | null>(null);
  const [canceling, setCanceling] = useState<MessageCampaignDto | null>(null);

  const list = useAsync(
    () =>
      backofficeApi.listMessageCampaigns({
        page,
        size: pageSize,
        ...(statusFilter ? { status: statusFilter as MessageCampaignStatus } : {}),
      }),
    [page, pageSize, statusFilter]
  );

  const { data: instances } = useAsync(() => backofficeApi.listMessagingInstances(), []);
  const { data: templates } = useAsync(() => backofficeApi.listMessageTemplates(), []);
  const { data: contactLists } = useAsync(() => backofficeApi.listContactLists(), []);

  const activeInstances = useMemo(() => (instances ?? []).filter((i) => i.active), [instances]);
  const activeTemplates = useMemo(() => (templates ?? []).filter((t) => t.active), [templates]);
  const activeLists = useMemo(() => (contactLists ?? []).filter((l) => l.active), [contactLists]);

  const selectedTemplate = useMemo(
    () => activeTemplates.find((t) => t.id === form.templateId),
    [activeTemplates, form.templateId]
  );

  const effectiveBody = form.body.trim() || selectedTemplate?.body || '';

  const selectedList = useMemo(
    () => activeLists.find((l) => l.id === form.contactListId),
    [activeLists, form.contactListId]
  );

  const chosenInstance = useMemo(
    () => activeInstances.find((i) => i.id === form.instanceId),
    [activeInstances, form.instanceId]
  );

  /** Espelha a validação do backend para o operador ver o problema antes de salvar. */
  const scheduleProblem = describeScheduleProblem(form.scheduledAt, chosenInstance);

  /** O formulário já sabe que falta a audiência; não precisa do 400 do backend. */
  const audienceReady =
    form.audienceType === 'CONTACT_LIST' ? !!form.contactListId : form.leadStatuses.length > 0;

  /** Estimativa mostrada antes de criar: evita descobrir depois que a lista não sai hoje. */
  const estimate = useMemo(() => {
    if (!chosenInstance || form.audienceType !== 'CONTACT_LIST' || !selectedList) return null;
    const recipients = selectedList.membersCount;
    if (recipients === 0) return null;
    const averageSeconds = (chosenInstance.minIntervalSeconds + chosenInstance.maxIntervalSeconds) / 2;
    const minutes = Math.round((recipients * averageSeconds) / 60);
    const days = Math.ceil(recipients / chosenInstance.dailyLimit);
    return { recipients, minutes, days };
  }, [chosenInstance, form.audienceType, selectedList]);

  useEffect(() => {
    if (!isOpen || !effectiveBody.trim()) {
      setRendered('');
      return undefined;
    }
    const timer = setTimeout(() => {
      backofficeApi
        .previewMessageTemplate({ body: effectiveBody })
        .then((result) => setRendered(result.rendered))
        .catch(() => setRendered(''));
    }, 350);
    return () => clearTimeout(timer);
  }, [effectiveBody, isOpen]);

  const openCreate = (): void => {
    setForm({ ...EMPTY_FORM, instanceId: activeInstances[0]?.id ?? '' });
    setRendered('');
    setIsOpen(true);
  };

  const toggleLeadStatus = (status: LeadStatus): void =>
    setForm((prev) => ({
      ...prev,
      leadStatuses: prev.leadStatuses.includes(status)
        ? prev.leadStatuses.filter((s) => s !== status)
        : [...prev.leadStatuses, status],
    }));

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      const payload: MessageCampaignRequestDto = {
        name: form.name,
        instanceId: form.instanceId,
        audienceType: form.audienceType,
        ...(form.templateId ? { templateId: form.templateId } : {}),
        ...(form.body.trim() ? { body: form.body.trim() } : {}),
        ...(form.audienceType === 'CONTACT_LIST' ? { contactListId: form.contactListId } : {}),
        ...(form.audienceType === 'LEADS' ? { leadStatuses: form.leadStatuses } : {}),
        excludePartners: form.excludePartners,
      };
      const created = await backofficeApi.createMessageCampaign(payload);

      // Agendamento em uma segunda chamada porque ele exige a permissão de
      // disparo, que a criação não exige. Se ela falhar, o rascunho continua de pé
      // e a data pode ser corrigida no drawer - por isso o erro é avisado sem
      // desfazer a campanha, que custou a resolução da audiência inteira.
      const scheduledIso = canSend ? toIsoFromInput(form.scheduledAt) : null;
      let scheduled = false;
      if (scheduledIso) {
        try {
          await backofficeApi.scheduleMessageCampaign(created.id, scheduledIso);
          scheduled = true;
        } catch (err) {
          showNotification(
            extractErrorMessage(err, 'Campanha criada, mas o agendamento não foi aceito.'),
            'error'
          );
        }
      }

      // O número de ignorados vale a menção: sem ele, uma campanha de 350 contatos
      // que resolveu 12 na fila parece um bug de audiência, quando na verdade os
      // outros 338 já receberam esta mensagem.
      const skipped =
        created.counters.skipped > 0
          ? ` ${formatNumber(created.counters.skipped)} ignorado(s) - opt-out${
              form.excludePartners ? ', parceiros' : ''
            } ou já contatados com esta mensagem.`
          : '';
      showNotification(
        (scheduled
          ? `Campanha criada com ${formatNumber(created.counters.pending)} destinatário(s) na fila e agendada. Nada sai antes da hora marcada.`
          : `Campanha criada com ${formatNumber(created.counters.pending)} destinatário(s) na fila. Nada foi enviado ainda.`) +
          skipped,
        'success'
      );
      setIsOpen(false);
      setSelectedId(created.id);
      list.reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível criar a campanha.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Iniciar, pausar e cancelar direto da linha.
   *
   * Também existem no drawer, mas exigir que o operador abra a campanha para pausar
   * cobra caro justamente no momento em que pausar importa: quando ele percebeu que a
   * mensagem saiu errada e quer parar o envio agora.
   */
  const act = async (
    campaign: MessageCampaignDto,
    action: 'start' | 'pause' | 'cancel' | 'retry',
    message: string
  ): Promise<void> => {
    setActing(campaign.id);
    try {
      if (action === 'start') await backofficeApi.startMessageCampaign(campaign.id);
      if (action === 'pause') await backofficeApi.pauseMessageCampaign(campaign.id);
      if (action === 'cancel') await backofficeApi.cancelMessageCampaign(campaign.id);
      if (action === 'retry') await backofficeApi.retryFailedMessageCampaign(campaign.id);
      showNotification(message, 'success');
      setCanceling(null);
      list.reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível concluir a ação.'), 'error');
    } finally {
      setActing(null);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    setRemoving(true);
    try {
      await backofficeApi.deleteMessageCampaign(deleting.id);
      showNotification('Campanha excluída.', 'success');
      setDeleting(null);
      list.reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível excluir a campanha.'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  const campaigns = list.data?.content ?? [];

  const hasRunning = campaigns.some((item) => item.status === 'RUNNING');
  const reloadList = list.reload;

  useEffect(() => {
    if (!hasRunning) return undefined;
    const timer = setInterval(() => reloadList(), LIST_POLL_MS);
    return () => clearInterval(timer);
  }, [hasRunning, reloadList]);

  return (
    <>
      <Toolbar>
        <Select
          options={STATUS_FILTER}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
        />
        <Button variant="ghost" onClick={reloadList} disabled={list.loading}>
          <RefreshCw size={16} /> Atualizar
        </Button>
        {canManage && (
          <Button variant="primary" onClick={openCreate} disabled={activeInstances.length === 0}>
            <Plus size={16} /> Nova campanha
          </Button>
        )}
      </Toolbar>

      {canManage && activeInstances.length === 0 && (
        <Warn>
          <AlertTriangle size={15} />
          Cadastre uma instância ativa na aba &quot;Instâncias&quot; antes de criar uma campanha.
        </Warn>
      )}

      <DataState
        loading={list.loading && !list.data}
        error={list.error}
        isEmpty={campaigns.length === 0}
        emptyTitle="Nenhuma campanha ainda"
        emptyDescription="Escolha a instância, a mensagem e a audiência. A campanha nasce em rascunho - nada sai antes de você iniciar."
        onRetry={list.reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Campanha</th>
                <th>Audiência</th>
                <th style={{ whiteSpace: 'nowrap' }}>Fila / enviadas</th>
                <th style={{ whiteSpace: 'nowrap' }}>Falhas</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Status</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.name}</strong>
                    <FieldHint>{item.instanceName}</FieldHint>
                    {/* A data agendada fica na linha, e não escondida no drawer: é a
                        diferença entre "essa campanha está parada" e "essa campanha
                        vai sair sozinha amanhã de manhã". */}
                    {item.scheduledAt && (
                      <Scheduled>
                        <Clock size={12} /> Inicia em {formatDateTime(item.scheduledAt)}
                      </Scheduled>
                    )}
                  </td>
                  <td>
                    {item.audienceType === 'CONTACT_LIST'
                      ? item.contactListName || 'Lista de contatos'
                      : `Leads: ${item.leadStatuses.map((s) => LEAD_STATUS_LABEL[s]).join(', ') || 'seleção'}`}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {formatNumber(item.counters.pending)} / {formatNumber(item.counters.sent)}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatNumber(item.counters.failed)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Badge tone={CAMPAIGN_STATUS_TONE[item.status]}>
                      {CAMPAIGN_STATUS_LABEL[item.status]}
                    </Badge>
                  </td>
                  <td>
                    <RowActions>
                      {canSend && (item.status === 'DRAFT' || item.status === 'PAUSED') && (
                        <ActionButton
                          title={item.status === 'DRAFT' ? 'Iniciar' : 'Retomar'}
                          aria-label={`${item.status === 'DRAFT' ? 'Iniciar' : 'Retomar'} campanha`}
                          color="#1E9E6A"
                          disabled={acting === item.id}
                          onClick={() =>
                            act(item, 'start', 'Campanha iniciada. O envio segue o ritmo da instância.')
                          }
                        >
                          <Play size={16} />
                        </ActionButton>
                      )}
                      {canSend && item.status === 'RUNNING' && (
                        <ActionButton
                          title="Pausar"
                          aria-label="Pausar campanha"
                          disabled={acting === item.id}
                          onClick={() => act(item, 'pause', 'Campanha pausada.')}
                        >
                          <Pause size={16} />
                        </ActionButton>
                      )}
                      {/* Só na campanha que tem falha: em toda campanha saudável
                          este botão seria ruído numa coluna já cheia de ações. */}
                      {canSend && item.status !== 'CANCELED' && item.counters.failed > 0 && (
                        <ActionButton
                          title={`Reenviar ${formatNumber(item.counters.failed)} falha(s)`}
                          aria-label="Reenviar destinatários com falha"
                          disabled={acting === item.id}
                          onClick={() =>
                            act(
                              item,
                              'retry',
                              'Destinatários com falha voltaram para a fila. Eles seguem o ritmo e a cota da instância.'
                            )
                          }
                        >
                          <RotateCcw size={16} />
                        </ActionButton>
                      )}
                      {canSend && item.status !== 'COMPLETED' && item.status !== 'CANCELED' && (
                        <ActionButton
                          title="Cancelar"
                          aria-label="Cancelar campanha"
                          color="#D97706"
                          disabled={acting === item.id}
                          onClick={() => setCanceling(item)}
                        >
                          <Ban size={16} />
                        </ActionButton>
                      )}
                      <ActionButton
                        title="Ver detalhes"
                        aria-label="Ver detalhes da campanha"
                        onClick={() => setSelectedId(item.id)}
                      >
                        <Eye size={16} />
                      </ActionButton>
                      {canManage && item.status !== 'RUNNING' && (
                        <ActionButton
                          title="Excluir"
                          aria-label="Excluir campanha"
                          color="#DC2626"
                          onClick={() => setDeleting(item)}
                        >
                          <Trash2 size={16} />
                        </ActionButton>
                      )}
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {(list.data?.totalPages ?? 0) > 1 && (
        <Pagination
          currentPage={page + 1}
          totalPages={list.data?.totalPages ?? 1}
          totalItems={list.data?.totalElements}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disabled={list.loading}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      )}

      <CampaignDetailDrawer
        campaignId={selectedId}
        canSend={canSend}
        onClose={() => setSelectedId(null)}
        onChanged={list.reload}
      />

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Nova campanha"
        width="660px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              isLoading={submitting}
              disabled={
                !form.name || !form.instanceId || !effectiveBody || !audienceReady || !!scheduleProblem
              }
            >
              Criar rascunho
            </Button>
          </>
        }
      >
        <TextInput
          label="Nome da campanha"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          placeholder="Ex.: Prospecção lojistas Toledo - outubro"
        />
        <Select
          label="Instância (número)"
          options={activeInstances.map((i) => ({
            value: i.id,
            label: `${i.name}${i.phoneNumber ? ` - ${i.phoneNumber}` : ''} · ${i.dailyLimit}/dia`,
          }))}
          value={form.instanceId}
          onChange={(e) => setForm((prev) => ({ ...prev, instanceId: e.target.value }))}
          containerStyle={{ marginTop: '0.75rem' }}
        />

        <Label>Audiência</Label>
        <SegmentedControl
          options={AUDIENCE_OPTIONS}
          value={form.audienceType}
          onChange={(value) => setForm((prev) => ({ ...prev, audienceType: value }))}
        />

        {form.audienceType === 'CONTACT_LIST' && (
          <Select
            label="Lista de contatos"
            options={[
              { value: '', label: 'Selecione uma lista' },
              ...activeLists.map((l) => ({
                value: l.id,
                label: `${l.name} (${formatNumber(l.membersCount)} contatos)`,
              })),
            ]}
            value={form.contactListId}
            onChange={(e) => setForm((prev) => ({ ...prev, contactListId: e.target.value }))}
            containerStyle={{ marginTop: '0.75rem' }}
          />
        )}

        {form.audienceType === 'LEADS' && (
          <>
            <Label>Colunas do Kanban a incluir</Label>
            <CheckboxGrid>
              {LEAD_STATUSES.map((status) => (
                <Checkbox
                  key={status}
                  label={LEAD_STATUS_LABEL[status]}
                  checked={form.leadStatuses.includes(status)}
                  onChange={() => toggleLeadStatus(status)}
                />
              ))}
            </CheckboxGrid>
            <FieldHint>
              O mesmo número em duas colunas recebe uma única mensagem. Leads com WhatsApp inválido são
              descartados na criação.
            </FieldHint>
          </>
        )}

        <Label>Recorte da audiência</Label>
        <Checkbox
          label="Não enviar para contatos parceiros"
          checked={form.excludePartners}
          onChange={(e) => setForm((prev) => ({ ...prev, excludePartners: e.target.checked }))}
        />
        <FieldHint>
          O contato cujo número é o de uma loja com parceria ativa entra na campanha como ignorado -
          aparece na conferência com o motivo, em vez de sumir da contagem. Loja apenas catalogada
          continua recebendo.
        </FieldHint>

        <Label>Mensagem</Label>
        <Select
          options={[
            { value: '', label: 'Escrever mensagem avulsa' },
            ...activeTemplates.map((t) => ({ value: t.id, label: t.name })),
          ]}
          value={form.templateId}
          onChange={(e) => setForm((prev) => ({ ...prev, templateId: e.target.value, body: '' }))}
        />
        {!form.templateId && (
          <TextArea
            value={form.body}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setForm((prev) => ({ ...prev, body: e.target.value }))
            }
            placeholder="Olá {{primeiro_nome}}, sou do Kardume Urbano..."
            style={{ marginTop: '0.6rem' }}
          />
        )}

        {rendered && (
          <>
            <Label>Como o destinatário vai receber</Label>
            <Preview>{rendered}</Preview>
          </>
        )}

        {estimate && (
          <FieldHint>
            {formatNumber(estimate.recipients)} contato(s) na lista · até {formatNumber(estimate.minutes)}{' '}
            min de envio
            {estimate.days > 1
              ? `, distribuídos em cerca de ${estimate.days} dias pelo teto diário da instância`
              : ''}
            . Contatos em opt-out e números repetidos são descartados na criação, então o total real pode
            ser menor - a contagem exata aparece assim que a campanha é criada.
          </FieldHint>
        )}

        {canSend && (
          <>
            <Label>Iniciar automaticamente (opcional)</Label>
            <DateTimeInput
              type="datetime-local"
              value={form.scheduledAt}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setForm((prev) => ({ ...prev, scheduledAt: e.target.value }))
              }
            />
            {scheduleProblem ? (
              <Warn>
                <AlertTriangle size={15} />
                {scheduleProblem}
              </Warn>
            ) : (
              <FieldHint>
                Em branco, a campanha espera alguém apertar &quot;Iniciar&quot;. Com data, ela entra em
                andamento sozinha na hora marcada
                {chosenInstance
                  ? `, dentro da janela ${shortTime(chosenInstance.windowStart)}–${shortTime(
                      chosenInstance.windowEnd
                    )} da instância`
                  : ''}
                .
              </FieldHint>
            )}
          </>
        )}

        <Warn>
          <AlertTriangle size={15} />
          Criar a campanha só monta a fila. Nada é enviado até alguém apertar &quot;Iniciar&quot; - ou
          até a hora agendada, se você marcar uma.
        </Warn>
      </Modal>

      <Modal
        isOpen={canceling !== null}
        onClose={() => setCanceling(null)}
        title="Cancelar campanha"
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCanceling(null)} disabled={acting === canceling?.id}>
              Voltar
            </Button>
            <Button
              variant="danger"
              onClick={() => canceling && act(canceling, 'cancel', 'Campanha cancelada.')}
              isLoading={acting === canceling?.id}
            >
              Cancelar campanha
            </Button>
          </>
        }
      >
        <ConfirmText>
          Cancelar <strong>{canceling?.name}</strong> descarta os{' '}
          {formatNumber(canceling?.counters.pending ?? 0)} destinatário(s) que ainda estão na fila. É
          irreversível - o que já foi enviado não é afetado.
        </ConfirmText>
      </Modal>

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Excluir campanha"
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={removing}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmDelete} isLoading={removing}>
              Excluir
            </Button>
          </>
        }
      >
        <ConfirmText>
          Excluir <strong>{deleting?.name}</strong> e o histórico de{' '}
          {formatNumber(deleting?.counters.total ?? 0)} destinatário(s) dela?
        </ConfirmText>
      </Modal>
    </>
  );
};

export default CampaignsTab;
